import { reaction } from "mobx"
import type { CodeStore } from "../store/store"
import { type LiveStatusAdapter, type LiveStatusSnapshot, getLiveStatusAdapter, serializeLiveStatusSnapshot } from "./liveStatus"
import { type MobileRunPhase, type MobileRunState, mobileRunPhaseFromStatus } from "./mobileRunState"
import type { PlatformListener } from "./types"

export const LIVE_STATUS_POLICY_EVENT = "wildlark:live-status-policy-changed"

export interface RunCandidate {
    nodeId: string
    workspaceId: string
    taskId: string
    title: string
    phase: MobileRunPhase
    startedAt?: string
    updatedAt: string
}

export interface LiveStatusCoordinatorOptions {
    nodeId: string
    computerName: string
    store: CodeStore
    adapter?: LiveStatusAdapter
    now?: () => Date
    publishState?: (state: MobileRunState) => Promise<boolean>
    isRemoteLiveStatusEnabled?: () => boolean
}

export class MobileRunSequencer {
    private states = new Map<string, MobileRunState>()

    next(candidate: RunCandidate): MobileRunState {
        const key = `${candidate.nodeId}\u0000${candidate.taskId}`
        const current = this.states.get(key)
        const parsedTimestamp = Date.parse(candidate.updatedAt)
        if (!Number.isFinite(parsedTimestamp)) throw new Error("Mobile run candidate has an invalid updatedAt timestamp")
        const timestampSequence = Math.max(1, Math.floor(parsedTimestamp))
        const changed =
            !current ||
            current.workspaceId !== candidate.workspaceId ||
            current.title !== candidate.title ||
            current.phase !== candidate.phase ||
            current.startedAt !== candidate.startedAt
        const sequence = current ? (changed ? Math.max(current.sequence + 1, timestampSequence) : current.sequence) : timestampSequence
        const state: MobileRunState = {
            version: 1,
            nodeId: candidate.nodeId,
            workspaceId: candidate.workspaceId,
            taskId: candidate.taskId,
            title: candidate.title,
            phase: candidate.phase,
            ...(candidate.startedAt ? { startedAt: candidate.startedAt } : {}),
            updatedAt: changed || !current ? candidate.updatedAt : current.updatedAt,
            sequence,
        }
        this.states.set(key, state)
        return state
    }
}

function priority(phase: MobileRunPhase): number {
    if (phase === "needs-input") return 6
    if (phase === "running") return 5
    if (phase === "queued") return 4
    if (phase === "failed") return 3
    if (phase === "cancelled") return 2
    return 1
}

function isOngoing(phase: MobileRunPhase): boolean {
    return phase === "queued" || phase === "running" || phase === "needs-input"
}

function preferCandidate(candidate: RunCandidate, current: RunCandidate): boolean {
    const candidateOngoing = isOngoing(candidate.phase)
    const currentOngoing = isOngoing(current.phase)
    if (candidateOngoing !== currentOngoing) return candidateOngoing
    if (candidateOngoing && priority(candidate.phase) !== priority(current.phase)) return priority(candidate.phase) > priority(current.phase)
    if (candidate.updatedAt !== current.updatedAt) return candidate.updatedAt > current.updatedAt
    return priority(candidate.phase) > priority(current.phase)
}

function latestCandidateByTask(options: LiveStatusCoordinatorOptions, includeTaskTitles: boolean): RunCandidate[] {
    const candidates: RunCandidate[] = []
    const tasks = options.store.productState?.tasks
    if (!tasks) return candidates

    for (const runtime of options.store.runtimes.runtimesById.values()) {
        const task = tasks.get(runtime.taskId)
        const phase = mobileRunPhaseFromStatus(runtime.status)
        if (!task || !phase) continue
        candidates.push({
            nodeId: options.nodeId,
            workspaceId: task.workspaceId,
            taskId: task.id,
            title: includeTaskTitles ? task.title : "WildLark task",
            phase,
            startedAt: runtime.startedAt,
            updatedAt: runtime.updatedAt,
        })
    }

    const latest = new Map<string, RunCandidate>()
    for (const candidate of candidates) {
        const current = latest.get(candidate.taskId)
        if (!current || preferCandidate(candidate, current)) {
            latest.set(candidate.taskId, candidate)
        }
    }
    return [...latest.values()]
}

function signature(options: LiveStatusCoordinatorOptions): string {
    return latestCandidateByTask(options, true)
        .sort((left, right) => left.taskId.localeCompare(right.taskId))
        .map((candidate) =>
            [candidate.taskId, candidate.workspaceId, candidate.title, candidate.phase, candidate.startedAt ?? "", candidate.updatedAt].join("\u0001")
        )
        .join("\u0002")
}

export function notifyLiveStatusPolicyChanged(): void {
    if (typeof window !== "undefined") window.dispatchEvent(new Event(LIVE_STATUS_POLICY_EVENT))
}

export class LiveStatusCoordinator {
    private readonly adapter: LiveStatusAdapter
    private readonly now: () => Date
    private readonly sequencer = new MobileRunSequencer()
    private readonly publisherSequencer = new MobileRunSequencer()
    private readonly lastPublishedSequences = new Map<string, number>()
    private aggregateSequence = 0
    private lastPresentation = ""
    private readonly trackedTaskIds = new Set<string>()
    private queue: Promise<void> = Promise.resolve()
    private disposed = false
    private lifecycleListener: PlatformListener | null = null
    private readonly policyListener = () => this.requestSync()
    private readonly disposeReaction: () => void

    constructor(private readonly options: LiveStatusCoordinatorOptions) {
        this.adapter = options.adapter ?? getLiveStatusAdapter()
        this.now = options.now ?? (() => new Date())
        this.disposeReaction = reaction(
            () => signature(options),
            () => this.requestSync(),
            { fireImmediately: true }
        )
        if (typeof window !== "undefined") window.addEventListener(LIVE_STATUS_POLICY_EVENT, this.policyListener)
        void this.adapter
            .addLifecycleListener((state) => {
                if (state !== "dismissed") return
                void this.adapter.setMonitoringEnabled(false).then(() => this.requestSync())
            })
            .then((listener) => {
                if (this.disposed) void listener.remove()
                else this.lifecycleListener = listener
            })
            .catch(() => undefined)
    }

    requestSync(): void {
        const run = () => this.sync()
        this.queue = this.queue.then(run, run)
    }

    async reconcileOnResume(): Promise<void> {
        this.requestSync()
        await this.queue
    }

    dispose(): void {
        this.disposed = true
        this.disposeReaction()
        if (typeof window !== "undefined") window.removeEventListener(LIVE_STATUS_POLICY_EVENT, this.policyListener)
        if (this.lifecycleListener) void this.lifecycleListener.remove()
    }

    private async sync(): Promise<void> {
        if (this.disposed) return
        const publicationCandidates = latestCandidateByTask(this.options, true)
        const publicationOngoing = publicationCandidates.filter((candidate) => isOngoing(candidate.phase))
        for (const candidate of publicationOngoing) this.trackedTaskIds.add(candidate.taskId)
        const publicationStates = publicationCandidates
            .filter((candidate) => isOngoing(candidate.phase) || this.trackedTaskIds.has(candidate.taskId))
            .map((candidate) => this.publisherSequencer.next(candidate))
            .sort((left, right) => left.updatedAt.localeCompare(right.updatedAt))
        if (this.options.publishState) {
            for (const state of publicationStates) {
                if ((this.lastPublishedSequences.get(state.taskId) ?? 0) >= state.sequence) continue
                try {
                    if (await this.options.publishState(state)) this.lastPublishedSequences.set(state.taskId, state.sequence)
                } catch (error) {
                    console.warn("[mobile-run-publisher] Semantic state publication failed", error instanceof Error ? error.message : String(error))
                }
            }
        }
        const titles = await this.adapter.getTaskTitlesEnabled()
        const candidates = latestCandidateByTask(this.options, titles.status === "ok" && titles.value)
        const ongoingCandidates = candidates.filter((candidate) => isOngoing(candidate.phase))
        for (const candidate of ongoingCandidates) this.trackedTaskIds.add(candidate.taskId)
        const relevantCandidates = candidates.filter((candidate) => isOngoing(candidate.phase) || this.trackedTaskIds.has(candidate.taskId))
        const states = relevantCandidates
            .map((candidate) => this.sequencer.next(candidate))
            .sort((left, right) => priority(right.phase) - priority(left.phase) || right.updatedAt.localeCompare(left.updatedAt))
        const active = states.filter((state) => state.phase === "queued" || state.phase === "running")
        const attention = states.filter((state) => state.phase === "needs-input")
        const topRun = attention[0] ?? active[0] ?? states[0]
        const currentTime = this.now()
        const now = currentTime.toISOString()
        const presentation = JSON.stringify({
            computerName: this.options.computerName,
            runningCount: active.length,
            attentionCount: attention.length,
            topRun: topRun
                ? {
                      nodeId: topRun.nodeId,
                      workspaceId: topRun.workspaceId,
                      taskId: topRun.taskId,
                      title: topRun.title,
                      phase: topRun.phase,
                      startedAt: topRun.startedAt,
                  }
                : null,
        })
        const reconciliation = await this.adapter.reconcile()
        if (reconciliation.status !== "ok") return

        this.aggregateSequence = Math.max(this.aggregateSequence + 1, Math.floor(currentTime.getTime()))
        const snapshot: LiveStatusSnapshot = {
            version: 1,
            sequence: this.aggregateSequence,
            computerName: this.options.computerName,
            runningCount: active.length,
            attentionCount: attention.length,
            updatedAt: now,
            ...(topRun ? { topRun } : {}),
        }
        serializeLiveStatusSnapshot(snapshot)

        if (this.options.isRemoteLiveStatusEnabled && !this.options.isRemoteLiveStatusEnabled()) {
            if (reconciliation.value.running) await this.adapter.end(snapshot)
            this.lastPresentation = ""
            this.trackedTaskIds.clear()
            return
        }

        const monitoring = await this.adapter.getMonitoringEnabled()
        if (monitoring.status !== "ok") return
        if (!monitoring.value) {
            if (reconciliation.value.running) await this.adapter.end(snapshot)
            this.lastPresentation = ""
            this.trackedTaskIds.clear()
            return
        }
        const availability = await this.adapter.getAvailability()
        if (availability.status !== "ok" || !availability.value.enabled) return
        if (presentation === this.lastPresentation && reconciliation.value.running) return

        if (active.length > 0 || attention.length > 0) {
            const result = reconciliation.value.running ? await this.adapter.update(snapshot) : await this.adapter.start(snapshot)
            if (result.status === "ok" && result.value !== "not-running") this.lastPresentation = presentation
            return
        }
        if (reconciliation.value.running) await this.adapter.end(snapshot)
        this.lastPresentation = presentation
        this.trackedTaskIds.clear()
    }
}
